import * as React from 'react';
import styled from 'styled-components';
import gettext, { ngettext } from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import useAuth from '@sdk/authentication/useAuth';
import displayNumberOrReplacer from '@sdk/utils/displayNumberOrReplacer';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import type { UpdateLaborCostSettings } from '@sdk/labor/types';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { FIELD_WIDTH_100 } from '../../constants';
import { TabType } from '../../types';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_100}px;
`;

const StyledRow = styled(Row)`
  justify-content: flex-end;
  align-items: center;
`;

type Props = {
  overtimeEnabled: boolean;
  overtimeWeekThreshold: number | string;
  overtimeWeekMultiplier: number | string;
  overtimeDayEnabled: boolean;
  overtimeDayThreshold: number | string;
  overtimeDayMultiplier: number | string;
  overtimeDoubleDailyEnabled: boolean;
  onLaborCostSettingsChange: UpdateLaborCostSettings;
};

const Overtime: React.FC<React.PropsWithChildren<Props>> = ({
  overtimeEnabled,
  overtimeWeekThreshold,
  overtimeWeekMultiplier,
  overtimeDayEnabled,
  overtimeDayThreshold,
  overtimeDayMultiplier,
  overtimeDoubleDailyEnabled,
  onLaborCostSettingsChange,
}) => {
  const { toggleError } = useSettingsErrorsContext();
  const { currentUser } = useAuth();

  const validateOvertime = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
    });
  };

  return (
    <>
      <SettingsSection withSeparator>
        <CollapsibleSettingsSection
          isActive={overtimeEnabled}
          headerItem={
            <SettingsSwitcherRow
              title={gettext('Weekly overtime')}
              value={overtimeEnabled}
              onChange={(value: boolean): void =>
                onLaborCostSettingsChange({ overtimeEnabled: value })
              }
              disabled={!currentUser.isAdmin}
            />
          }
          contentItem={
            <RowSpaceBetween pTop={20}>
              <Text14 themeColor={ThemeColorType.gray}>
                <TranslateWithComponents
                  replaceComponents={{
                    overtimeWeekThreshold: (
                      <Text14 themeColor={ThemeColorType.primary}>
                        {displayNumberOrReplacer(overtimeWeekThreshold, 'X')}
                      </Text14>
                    ),
                    overtimeWeekMultiplier: (
                      <Text14 themeColor={ThemeColorType.primary}>
                        {displayNumberOrReplacer(overtimeWeekMultiplier, 'X')}
                      </Text14>
                    ),
                  }}>
                  {ngettext(
                    'Weekly overtime starts after [[overtimeWeekThreshold]] hour at rate [[overtimeWeekMultiplier]].',
                    'Weekly overtime starts after [[overtimeWeekThreshold]] hours at rate [[overtimeWeekMultiplier]].',
                    Number(overtimeWeekThreshold)
                  )}
                </TranslateWithComponents>
              </Text14>

              <StyledRow>
                <StyledTextField
                  skipErrorMessage
                  disabled={!currentUser.isAdmin}
                  postfix={gettext('Hours')}
                  value={overtimeWeekThreshold}
                  onChange={(event): void => {
                    onLaborCostSettingsChange({
                      overtimeWeekThreshold: event.currentTarget.value,
                    });
                    toggleError(
                      TabType.laborCost,
                      'overtimeWeekThreshold',
                      !validateOvertime(event.currentTarget.value)
                    );
                  }}
                  invalid={!validateOvertime(overtimeWeekThreshold)}
                />
                <Row>
                  <Text14 themeColor={ThemeColorType.gray} pRight={10} pLeft={10} bold>
                    {gettext('@ RATE X')}
                  </Text14>
                </Row>
                <StyledTextField
                  skipErrorMessage
                  disabled={!currentUser.isAdmin}
                  value={overtimeWeekMultiplier}
                  onChange={(event): void => {
                    onLaborCostSettingsChange({
                      overtimeWeekMultiplier: event.currentTarget.value,
                    });
                    toggleError(
                      TabType.laborCost,
                      'overtimeWeekMultiplier',
                      !validateOvertime(event.currentTarget.value)
                    );
                  }}
                  invalid={!validateOvertime(overtimeWeekMultiplier)}
                />
              </StyledRow>
            </RowSpaceBetween>
          }
        />
      </SettingsSection>
      <SettingsSection withSeparator>
        <CollapsibleSettingsSection
          isActive={overtimeDayEnabled}
          headerItem={
            <SettingsSwitcherRow
              title={gettext('Daily overtime')}
              value={overtimeDayEnabled}
              onChange={(value: boolean): void =>
                onLaborCostSettingsChange({ overtimeDayEnabled: value })
              }
              disabled={!currentUser.isAdmin}
            />
          }
          contentItem={
            <RowSpaceBetween pTop={20}>
              <Text14 themeColor={ThemeColorType.gray}>
                <TranslateWithComponents
                  replaceComponents={{
                    overtimeDayThreshold: (
                      <Text14 themeColor={ThemeColorType.primary}>
                        {displayNumberOrReplacer(overtimeDayThreshold, 'X')}
                      </Text14>
                    ),
                    overtimeDayMultiplier: (
                      <Text14 themeColor={ThemeColorType.primary}>
                        {displayNumberOrReplacer(overtimeDayMultiplier, 'X')}
                      </Text14>
                    ),
                  }}>
                  {ngettext(
                    'Daily overtime starts after [[overtimeDayThreshold]] hour at rate [[overtimeDayMultiplier]].',
                    'Daily overtime starts after [[overtimeDayThreshold]] hours at rate [[overtimeDayMultiplier]].',
                    Number(overtimeDayThreshold)
                  )}
                </TranslateWithComponents>
              </Text14>

              <StyledRow>
                <StyledTextField
                  skipErrorMessage
                  disabled={!currentUser.isAdmin}
                  postfix={gettext('Hours')}
                  value={overtimeDayThreshold}
                  onChange={(event): void => {
                    onLaborCostSettingsChange({
                      overtimeDayThreshold: event.currentTarget.value,
                    });
                    toggleError(
                      TabType.laborCost,
                      'overtimeDayThreshold',
                      !validateOvertime(event.currentTarget.value)
                    );
                  }}
                  invalid={!validateOvertime(overtimeDayThreshold)}
                />
                <Row>
                  <Text14 themeColor={ThemeColorType.gray} pRight={10} pLeft={10} bold>
                    {gettext('@ RATE X')}
                  </Text14>
                </Row>
                <StyledTextField
                  skipErrorMessage
                  disabled={!currentUser.isAdmin}
                  value={overtimeDayMultiplier}
                  onChange={(event): void => {
                    onLaborCostSettingsChange({
                      overtimeDayMultiplier: event.currentTarget.value,
                    });
                    toggleError(
                      TabType.laborCost,
                      'overtimeDayMultiplier',
                      !validateOvertime(event.currentTarget.value)
                    );
                  }}
                  invalid={!validateOvertime(overtimeDayMultiplier)}
                />
              </StyledRow>
            </RowSpaceBetween>
          }
        />
      </SettingsSection>
      <SettingsSwitcherRow
        pBottom={20}
        title={gettext('Daily double overtime')}
        subtitle={gettext('Employees will receive 2X their regular pay after 12 hours in a day.')}
        value={overtimeDoubleDailyEnabled}
        onChange={(value: boolean): void =>
          onLaborCostSettingsChange({ overtimeDoubleDailyEnabled: value })
        }
        disabled={!currentUser.isAdmin}
      />
    </>
  );
};

export default Overtime;
